import type { Question } from '../data/types';
import { shuffle, type Rng } from './shuffle';

/** A question prepared for display: prompt plus shuffled answer choices. */
export interface PlayQuestion {
  question: Question;
  choices: string[];
}

/** Shuffle the correct + incorrect answers into display order. */
export function makePlayQuestion(q: Question, rng: Rng = Math.random): PlayQuestion {
  return { question: q, choices: shuffle([q.correctAnswer, ...q.incorrectAnswers], rng) };
}

/**
 * Endless "play until you miss" quiz engine. Serves questions without repeats
 * until the pool is exhausted, then reshuffles. Pure of any DOM/storage so it
 * can be unit-tested directly.
 */
export class Quiz {
  private used = new Set<string>();
  score = 0;
  over = false;

  constructor(
    private readonly pool: readonly Question[],
    private readonly rng: Rng = Math.random,
  ) {
    if (pool.length === 0) throw new Error('Quiz requires a non-empty question pool');
  }

  /** Draw the next unseen question, recycling the pool once everything is used. */
  next(): PlayQuestion {
    let available = this.pool.filter((q) => !this.used.has(q.id));
    if (available.length === 0) {
      this.used.clear();
      available = [...this.pool];
    }
    const picked = available[Math.floor(this.rng() * available.length)];
    this.used.add(picked.id);
    return makePlayQuestion(picked, this.rng);
  }

  /**
   * Record an answer. Correct -> score increments and play continues.
   * Wrong -> the run is over. Returns whether the answer was correct.
   */
  answer(choice: string, current: PlayQuestion): boolean {
    if (this.over) return false;
    const correct = choice === current.question.correctAnswer;
    if (correct) {
      this.score += 1;
    } else {
      this.over = true;
    }
    return correct;
  }
}
